// src/shared/GlassSnack.jsx
import * as React from "react";
import { Paper, Box, Typography, IconButton } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTheme, alpha } from "@mui/material/styles";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";

const GlassSnack = React.forwardRef(function GlassSnack(props, ref) {
  const {
    id,
    message,
    variant,
    className,
    style,
    onMouseEnter,
    onMouseLeave,
  } = props;

  const theme = useTheme();
  const { closeSnackbar } = useSnackbar();

  const Icon =
    variant === "success" ? CheckCircleRoundedIcon :
    variant === "error"   ? ErrorRoundedIcon :
    variant === "warning" ? WarningRoundedIcon :
                            InfoRoundedIcon;

  return (
    <Paper
      ref={ref}
      role="alert"
      className={className}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      elevation={0}
      sx={{
        backdropFilter: "saturate(160%) blur(16px)",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(145deg, rgba(255,255,255,.12), rgba(255,255,255,.05))"
            : "linear-gradient(145deg, rgba(255,255,255,.65), rgba(255,255,255,.35))",

        border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.35 : 0.55)}`,
        boxShadow:
          theme.palette.mode === "dark"
            ? `inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.06)},
               0 10px 30px ${alpha(theme.palette.common.black, 0.4)}`
            : `inset 0 0 0 1px ${alpha(theme.palette.common.black, 0.05)},
               0 8px 18px ${alpha(theme.palette.common.black, 0.15)}`,
        
        color: theme.palette.text.primary,
        borderRadius: 2,
        px: 1.25,
        py: 1,
        minWidth: 280,
        maxWidth: 420,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          width: 28,
          height: 28,
          borderRadius: "50%",
          background:
            variant === "success"
              ? alpha(theme.palette.success.main, 0.35)
              : variant === "error"
              ? alpha(theme.palette.error.main, 0.35)
              : variant === "warning"
              ? alpha(theme.palette.warning.main, 0.35)
              : alpha(theme.palette.info.main, 0.35),
          color: theme.palette.getContrastText(theme.palette.background.paper),
        }}
      >
        <Icon fontSize="small" />
      </Box>

      <Typography variant="body2" sx={{ flex: 1, pr: 1 }}>
        {typeof message === "string" ? message : String(message)}
      </Typography>

      <IconButton
        size="small"
        onClick={() => closeSnackbar(id)}
        sx={{
          color: "inherit",
          "&:hover": {
            backgroundColor: alpha(theme.palette.text.primary, 0.12),
          },
        }}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
});

export default GlassSnack;
